/**
 * Landing FAQ — bilingual eight-question accessible accordion.
 *
 * Renders directly before the final ``Stop guessing. Start
 * analyzing.`` CTA on the landing page.  Copy is fully i18n-driven
 * (see landing.faq_q1_q .. landing.faq_q8_a in en.js / de.js).
 *
 * Accessibility:
 *   * Each question is a semantic <button> with aria-expanded and
 *     aria-controls wiring to a uniquely-IDed answer panel.
 *   * The answer panel keeps its DOM node mounted (hidden via
 *     ``hidden``) so future SEO/screen-readers do not lose content.
 *   * Keyboard: Enter / Space toggle each item.
 *   * Respects prefers-reduced-motion via ``motion-reduce:transition-none``.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useTranslation } from "@/lib/i18n";

const QUESTION_KEYS = [
  ["faq_q1_q", "faq_q1_a"],
  ["faq_q2_q", "faq_q2_a"],
  ["faq_q3_q", "faq_q3_a"],
  ["faq_q4_q", "faq_q4_a"],
  ["faq_q5_q", "faq_q5_a"],
  ["faq_q6_q", "faq_q6_a"],
  ["faq_q7_q", "faq_q7_a"],
  ["faq_q8_q", "faq_q8_a"],
];

export default function LandingFAQ() {
  const { t } = useTranslation();
  // FAQ 1 is open by default so the free-usage offer is immediately
  // visible without a click.
  const [openIx, setOpenIx] = useState(0);

  const handleToggle = (ix) => {
    setOpenIx((prev) => (prev === ix ? -1 : ix));
  };

  return (
    <section
      className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-24"
      data-testid="landing-faq"
      aria-labelledby="landing-faq-heading"
    >
      <div className="text-center max-w-2xl mx-auto">
        <span
          data-testid="landing-faq-eyebrow"
          className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-muted-foreground/80 font-medium"
        >
          {t("landing.faq_eyebrow")}
        </span>
        <h2
          id="landing-faq-heading"
          data-testid="landing-faq-heading"
          className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground"
        >
          {t("landing.faq_title")}
        </h2>
        <p
          data-testid="landing-faq-subtitle"
          className="mt-2.5 text-sm sm:text-base text-muted-foreground/90 leading-relaxed"
        >
          {t("landing.faq_subtitle")}
        </p>
      </div>

      <ul
        className="mt-8 sm:mt-10 flex flex-col gap-2"
        data-testid="landing-faq-list"
      >
        {QUESTION_KEYS.map(([qKey, aKey], ix) => {
          const isOpen = openIx === ix;
          const buttonId = `landing-faq-q${ix + 1}-button`;
          const panelId = `landing-faq-q${ix + 1}-panel`;
          return (
            <li
              key={qKey}
              className={`rounded-xl border bg-card/40 backdrop-blur-sm transition-colors motion-reduce:transition-none ${
                isOpen
                  ? "border-[#3FAE89]/40 bg-[#3FAE89]/[0.04]"
                  : "border-border/60 hover:border-border"
              }`}
              data-testid={`landing-faq-item-${ix + 1}`}
              data-open={isOpen ? "true" : "false"}
            >
              <h3 className="m-0">
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => handleToggle(ix)}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 sm:px-6 sm:py-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3FAE89]/60 rounded-xl"
                  data-testid={`landing-faq-q${ix + 1}-button`}
                >
                  <span className="text-[15px] sm:text-base font-medium text-foreground leading-snug pr-2">
                    {t(`landing.${qKey}`)}
                  </span>
                  <span
                    aria-hidden="true"
                    className={`shrink-0 flex items-center justify-center w-7 h-7 rounded-full border transition-transform motion-reduce:transition-none ${
                      isOpen
                        ? "border-[#3FAE89]/60 text-[#3FAE89] rotate-45"
                        : "border-border/60 text-muted-foreground/80"
                    }`}
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                  </span>
                </button>
              </h3>
              <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                hidden={!isOpen}
                data-testid={`landing-faq-q${ix + 1}-answer`}
                className="px-5 sm:px-6 pb-5 sm:pb-6 -mt-1 text-[14px] sm:text-[15px] text-muted-foreground/95 leading-relaxed"
              >
                <FaqAnswer aKey={aKey} />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// Renders the answer for a given translation key. FAQ 8's answer
// mentions the Pricing page — that phrase is turned into an
// internal <Link to="/pricing"> using the project's normal
// navigation behaviour without duplicating the pricing table.
function FaqAnswer({ aKey }) {
  const { t } = useTranslation();
  const answer = t(`landing.${aKey}`);
  if (aKey !== "faq_q8_a") return <p className="m-0">{answer}</p>;
  // Split around the localised "Pricing page" phrase and wrap the
  // match in a Link.  Both EN ("Pricing page") and DE
  // ("Pricing-Seite") are supported.
  const linkPhrase = t("landing.faq_q8_pricing_link_text");
  const ix = answer.indexOf(linkPhrase);
  if (ix < 0) return <p className="m-0">{answer}</p>;
  return (
    <p className="m-0">
      {answer.slice(0, ix)}
      <Link
        to="/pricing"
        className="underline underline-offset-2 decoration-[#3FAE89]/50 hover:decoration-[#3FAE89] text-foreground/95"
        data-testid="landing-faq-pricing-link"
      >
        {linkPhrase}
      </Link>
      {answer.slice(ix + linkPhrase.length)}
    </p>
  );
}
